// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { Id } from '@/convex/_generated/dataModel';
import { CopyIcon } from '@radix-ui/react-icons';
import { useConfetti } from '@/hooks/useConfetti';
import { truncateKey } from '@/utils/truncateKey';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Profile } from '@/types/profiles';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ProfileCardEnhanced } from '@/components/gamification/ProfileCardEnhanced';
import { ReactBitsProfileCard } from '@/components/gamification/ReactBitsProfileCard';
import { ProfileCard3D } from '@/components/gamification/ProfileCard3D';
import '@/components/gamification/ProfileCard3D.css';
import { RefreshCw } from 'lucide-react';

interface ProfileGroup {
	_id: Id<'profileGroups'>;
	name: string;
	description?: string;
	isDefault?: boolean;
}

export function SocialProfiles() {
	const { user, isAdmin, isSuperAdmin, walletAddress } = useAuth();
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const [newProfileTitle, setNewProfileTitle] = React.useState('');
	const [userEmail, setUserEmail] = React.useState('');
	const [error, setError] = React.useState<string | null>(null);
	const [isCommunity, setIsCommunity] = React.useState(false);
	const createProfileMutation = useMutation(api.profiles.create);
	const checkProfileTitle = useQuery(api.profiles.checkTitleExists, {
		profileTitle: newProfileTitle,
	});
	const [isCreating, setIsCreating] = React.useState(false);
	const generateProfileUrlAction = useAction(api.uploadPost.generateProfileUrl);
	const createProfileAction = useAction(api.uploadPost.createProfile);
	const profileGroups = useQuery(api.profileGroups.list, {
		userId: user?.id as Id<'users'> | undefined,
		isSuperAdmin: user?.isSuperAdmin || false,
	});
	const createDefaultGroupsMutation = useMutation(
		api.profileGroups.createDefaultGroups
	);
	const profiles = useQuery(
		api.profiles.list,
		user?.id
			? {
					userId: user.id as any,
					isSuperAdmin: isSuperAdmin || false,
				}
			: 'skip'
	) as Profile[] | undefined;
	const [expandedGroups, setExpandedGroups] = React.useState<{
		[key: string]: boolean;
	}>({});
	const regenerateProfileUrlMutation = useAction(
		api.uploadPost.regenerateProfileUrl
	);
	const [regeneratingIds, setRegeneratingIds] = React.useState<Set<string>>(
		new Set()
	);
	const refreshProfileDetailsAction = useAction(
		api.uploadPost.refreshProfileDetails
	);
	const [isRefreshing, setIsRefreshing] = React.useState(false);
	const refreshTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
	const [hasRefreshed, setHasRefreshed] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState('');
	const [fetchingAnalytics, setFetchingAnalytics] = React.useState<
		Set<string>
	>(new Set());
	const fetchProfileAnalyticsAction = useAction(
		api.uploadPost.fetchProfileAnalytics
	);

	// Get analytics for all profiles
	const profileAnalytics = useQuery(
		api.profileAnalytics.getAllProfileAnalytics,
		profiles && profiles.length > 0 ? {} : 'skip'
	);

	// Refresh all profiles when component mounts with a logged-in user
	React.useEffect(() => {
		// Skip if already refreshed, no user, no profiles, is super admin, or currently refreshing
		if (
			hasRefreshed ||
			!user ||
			!profiles ||
			isSuperAdmin ||
			isRefreshing
		) {
			return;
		}

		const refreshProfilesSequentially = async () => {
			setIsRefreshing(true);
			try {
				for (const profile of profiles) {
					try {
						// Add a delay between each refresh to avoid overwhelming the connection
						await new Promise((resolve) =>
							setTimeout(resolve, 500)
						);
						await refreshProfileDetailsAction({
							profileId: profile._id,
						});
					} catch (error) {
						console.warn(
							`Failed initial refresh for profile ${profile.profileTitle}, will retry once:`,
							error
						);
						try {
							// Wait a bit longer before retry
							await new Promise((resolve) =>
								setTimeout(resolve, 2000)
							);
							await refreshProfileDetailsAction({
								profileId: profile._id,
							});
						} catch (retryError) {
							console.error(
								`Failed to refresh profile ${profile.profileTitle} after retry:`,
								retryError
							);
						}
					}
				}
			} finally {
				setIsRefreshing(false);
				setHasRefreshed(true); // Mark as refreshed
			}
		};

		void refreshProfilesSequentially();

		// Cleanup any pending timeouts
		const timeoutId = refreshTimeoutRef.current;
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [
		user?.id,
		profiles?.length,
		isSuperAdmin,
		isRefreshing,
		refreshProfileDetailsAction,
		hasRefreshed,
		user,
		profiles,
	]);

	// Filter profiles based on user permissions
	const filteredProfiles = React.useMemo(() => {
		if (!profiles) return undefined;
		if (isSuperAdmin) return profiles; // Super admins see all profiles
		if (isAdmin) {
			// Regular admins see profiles they created AND profiles with their email
			return profiles.filter(
				(profile) =>
					profile.userId === user?.id ||
					profile.email.toLowerCase() === user?.email?.toLowerCase()
			);
		}
		return []; // Non-admins see no profiles (though this case shouldn't occur due to routing)
	}, [profiles, isAdmin, isSuperAdmin, user?.id, user?.email]);

	// Filter profiles based on search term
	const searchFilteredProfiles = React.useMemo(() => {
		if (!filteredProfiles || !searchTerm.trim()) return filteredProfiles;

		const term = searchTerm.toLowerCase();
		return filteredProfiles.filter(
			(profile) =>
				profile.profileTitle.toLowerCase().includes(term) ||
				profile.email.toLowerCase().includes(term) ||
				profile.profileKey.toLowerCase().includes(term)
		);
	}, [filteredProfiles, searchTerm]);

	// Check if user has reached their profile limit
	// const hasReachedProfileLimit = React.useMemo(() => {
	// 	if (isSuperAdmin) return false;
	// 	return (filteredProfiles?.length || 0) >= 3;
	// }, [filteredProfiles?.length, isSuperAdmin]);

	// Group profiles by ownership (My Profiles, Community Profiles, All Profiles for SuperAdmin)
	const groupedProfiles = React.useMemo(() => {
		if (!searchFilteredProfiles || !user?.id) return {};

		const grouped: Record<
			string,
			{
				groupName: string;
				profiles: Profile[];
				canEdit: boolean;
			}
		> = {};

		// Initialize groups
		if (isSuperAdmin) {
			// SuperAdmin sees all profiles grouped by ownership
			grouped['my-profiles'] = {
				groupName: 'My Profiles',
				profiles: [],
				canEdit: true,
			};
			grouped['community-profiles'] = {
				groupName: 'Community Profiles',
				profiles: [],
				canEdit: true, // SuperAdmin can edit all
			};
			grouped['other-profiles'] = {
				groupName: 'Other User Profiles',
				profiles: [],
				canEdit: true, // SuperAdmin can edit all
			};
		} else {
			// Regular users see their profiles and community profiles
			grouped['my-profiles'] = {
				groupName: 'My Profiles',
				profiles: [],
				canEdit: true,
			};
			grouped['community-profiles'] = {
				groupName: 'Community Profiles',
				profiles: [],
				canEdit: false, // Regular users cannot edit community profiles
			};
		}

		// Group profiles
		searchFilteredProfiles.forEach((profile) => {
			const isMyProfile =
				(profile.userId as unknown as string) ===
				(user.id as unknown as string);
			const isCommunityProfile = profile.isCommunity === true;

			if (isMyProfile) {
				grouped['my-profiles'].profiles.push(profile);
			} else if (isCommunityProfile) {
				grouped['community-profiles'].profiles.push(profile);
			} else if (isSuperAdmin) {
				// Only SuperAdmin sees other users' non-community profiles
				grouped['other-profiles'].profiles.push(profile);
			}
		});

		// Remove empty groups
		Object.keys(grouped).forEach((key) => {
			if (grouped[key].profiles.length === 0) {
				delete grouped[key];
			}
		});

		return grouped;
	}, [searchFilteredProfiles, user, isSuperAdmin]);

	React.useEffect(() => {
		if (profileGroups?.length === 0) {
			console.log('Creating default groups...');
			createDefaultGroupsMutation();
		}
	}, [profileGroups, createDefaultGroupsMutation]);

	// Handle fetching analytics for a profile
	const handleFetchAnalytics = async (profile: Profile) => {
		if (!profile.profileKey) return;

		setFetchingAnalytics((prev) => new Set(prev).add(profile._id));
		try {
			await fetchProfileAnalyticsAction({
				profileId: profile._id,
			});
			toast({
				title: '✨ Analytics Updated ✨',
				description:
					'Profile analytics have been refreshed from social platforms',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description: 'Failed to fetch analytics',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setFetchingAnalytics((prev) => {
				const next = new Set(prev);
				next.delete(profile._id);
				return next;
			});
		}
	};

	// Auto-fetch analytics for profiles without data - DISABLED for now
	// React.useEffect(() => {
	// 	if (!profiles || !profileAnalytics || fetchingAnalytics.size > 0) return;

	// 	const profilesToFetch = profiles.filter(profile => {
	// 		if (!profile.profileKey) return false;

	// 		// Check if profile has any analytics data
	// 		const hasAnalytics = profileAnalytics.some(analytics => analytics.profileId === profile._id);
	// 		return !hasAnalytics;
	// 	});

	// 	// Fetch analytics for profiles without data (max 3 at once to avoid overwhelming)
	// 	profilesToFetch.slice(0, 3).forEach(profile => {
	// 		if (profile.profileKey) {
	// 			handleFetchAnalytics(profile);
	// 		}
	// 	});
	// }, [profiles, profileAnalytics, fetchingAnalytics.size]);

	const handleRegenerateUrl = async (profile: Profile) => {
		try {
			// Set regenerating state before any API calls
			setRegeneratingIds((prev) => new Set([...prev, profile._id]));

			// Wait for all operations to complete
			// WHY callerWallet (SAR-01-14): action is auth-gated; admin wallet
			// authorizes server-side via the admin fallback.
			await regenerateProfileUrlMutation({
				profileId: profile._id,
				callerWallet: walletAddress ?? undefined,
			});
			await new Promise((resolve) => setTimeout(resolve, 2000));
			await refreshProfileDetailsAction({ profileId: profile._id });

			toast({
				title: '✨ Profile URL Updated ✨',
				description: 'Profile URL has been regenerated successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description:
					error instanceof Error
						? error.message
						: 'Failed to regenerate profile URL',
				variant: 'destructive',
				duration: 3000,
				style: { backgroundColor: '#FF8C00', color: 'white' },
			});
		} finally {
			// Clear regenerating state after everything is done
			setRegeneratingIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(profile._id);
				return newSet;
			});
		}
	};

	if (!user) {
		return <div>Please sign in to access this page</div>;
	}

	const handleCreateProfile = async () => {
		try {
			setIsCreating(true);
			setError(null);
			const errors: string[] = [];

			// Normalize email to lowercase first, before any checks
			const normalizedEmail = userEmail.toLowerCase().trim();

			// Basic validations
			if (!normalizedEmail) {
				errors.push('🔥 Email is required');
			}
			if (!newProfileTitle) {
				errors.push('🔥 Profile Title is required');
			}
			if (!user?.id) {
				errors.push('🔥 User not authenticated');
				return; // Return early if no user
			}

			// // Check profile limit for regular admins
			// if (!isSuperAdmin && hasReachedProfileLimit) {
			// 	errors.push(
			// 		'🔥 You have reached the maximum limit of 3 profiles (including profiles using your email)'
			// 	);
			// }

			// Strict email duplication check
			if (profiles && normalizedEmail) {
				const existingProfile = profiles.find(
					(profile) =>
						profile.email.toLowerCase().trim() === normalizedEmail
				);

				if (existingProfile) {
					errors.push(
						`🔥 This email is already connected to a profile with title: "${existingProfile.profileTitle}"`
					);
				}
			}

			// If any validation errors, show them and stop
			if (errors.length > 0) {
				setError(errors.join('\n'));
				errors.forEach((error) => {
					toast({
						title: '🔥 Validation Error 🔥',
						description: error,
						duration: 3000,
						variant: 'destructive',
						style: { backgroundColor: '#FF8C00', color: 'white' },
					});
				});
				return;
			}

			const userId = user.id;

			// Format the title consistently for the social profile provider.
			const formattedTitle = `${newProfileTitle} - ${normalizedEmail}`;

			console.log('Creating Upload Post profile:', {
				title: formattedTitle,
				email: normalizedEmail,
			});

			// Create the provider profile first so the local profile stores its provider username.
			const result = await createProfileAction({
				title: formattedTitle,
				email: normalizedEmail,
			});

			if (result?.success === false) {
				setError(result.error ?? 'Unknown error');
				toast({
					title: '🔥 Error 🔥',
					description: result.error,
					variant: 'destructive',
					duration: 5000,
					style: { backgroundColor: '#FF8C00', color: 'white' },
				});
				return;
			}

			const uploadPostProfile = result?.data ?? result;

			// Check if notification setup failed
			if (uploadPostProfile.webhookSetupFailed) {
				console.warn(
					'Upload Post notification setup failed:',
					uploadPostProfile.webhookError
				);
				toast({
					title: '⚠️ Partial Success ⚠️',
					description:
						'Profile created but notification setup failed. Some status updates may be limited.',
					duration: 5000,
					variant: 'default',
					style: { backgroundColor: '#FFA500', color: 'white' },
				});
			}

			// Upload Post username is stored in the existing profileKey field for v1 compatibility.
			const profileKey =
				uploadPostProfile.profileKey ||
				uploadPostProfile.username ||
				uploadPostProfile.profile_username;

			if (!profileKey) {
				throw new Error('Upload Post profile username was not returned');
			}

			console.log('Generating profile URL:', {
				profileKey,
				email: normalizedEmail,
			});

			// Generate a connection URL through Upload Post.
			const profileUrlResponse = await generateProfileUrlAction({
				profileKey,
				username: profileKey,
				email: normalizedEmail,
				// WHY callerWallet (SAR-01-14): gated action; at creation time no
				// local profiles row exists yet, so the server-side admin fallback
				// is the authorizing path.
				callerWallet: walletAddress ?? undefined,
			});
			const profileUrl =
				typeof profileUrlResponse === 'string'
					? profileUrlResponse
					: profileUrlResponse?.url ||
						profileUrlResponse?.access_url ||
						profileUrlResponse?.accessUrl ||
						profileUrlResponse?.profileUrl;

			if (!profileUrl) {
				throw new Error('Upload Post connection URL was not returned');
			}

			console.log('Creating Convex profile:', {
				email: normalizedEmail,
				title: newProfileTitle,
				profileKey,
			});

			// Save profile to Convex
			await createProfileMutation({
				email: normalizedEmail,
				profileTitle: newProfileTitle,
				profileKey: profileKey,
				profileUrl: profileUrl,
				webhookRefId:
					uploadPostProfile.webhookRefId ||
					uploadPostProfile.notificationId,
				userId: user.id as any,
				isCommunity: isCommunity,
			});

			setNewProfileTitle('');
			setUserEmail('');
			setError(null);
			setIsCommunity(false);

			triggerConfetti();
			toast({
				title: '✨ Profile Created ✨',
				description: 'Social profile successfully created',
				duration: 3000,
			});
		} catch (error) {
			console.error('Profile creation error:', error);

			// Clean up the error message by removing the technical prefix
			let errorMessage =
				error instanceof Error
					? error.message
					: 'Failed to create profile';

			// Remove the ConvexError prefix if present
			errorMessage = errorMessage.replace(
				/^(?:Uncaught )?(?:ConvexError|Error): /,
				''
			);

			// Set the error in the UI
			setError(errorMessage);

			// Show the toast
			toast({
				title: '🔥 Error 🔥',
				description: errorMessage,
				variant: 'destructive',
				duration: 5000,
				style: { backgroundColor: '#FF8C00', color: 'white' },
			});
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<div className='relative container mx-auto p-6 pb-80'>
			<style
				dangerouslySetInnerHTML={{
					__html: `
						.custom-scrollbar::-webkit-scrollbar {
							width: 8px;
						}
						.custom-scrollbar::-webkit-scrollbar-track {
							background: rgba(139, 92, 246, 0.1);
							border-radius: 4px;
						}
						.custom-scrollbar::-webkit-scrollbar-thumb {
							background: linear-gradient(
								180deg,
								#8b5cf6 0%,
								#ec4899 100%
							);
							border-radius: 4px;
						}
						.custom-scrollbar::-webkit-scrollbar-thumb:hover {
							background: linear-gradient(
								180deg,
								#7c3aed 0%,
								#db2777 100%
							);
						}
					`,
				}}
			/>
			<>
				<h1 className='text-3xl font-bold mb-2 pt-8'>
					Social Profiles Management
				</h1>
				{/* <p className='text-lg text-gray-500 mb-6 pb-8'>
					Active Profiles: {filteredProfiles?.length || 0}
					{!isSuperAdmin && (
						<span className='ml-2 text-orange-500'>
							(Maximum: 3 profiles)
						</span>
					)}
				</p> */}
			</>
			<div className='container mx-auto p-6'>
				<div className='mb-8'>
					<Card>
						<CardHeader className='mb-8'>
							<div className='flex justify-between items-center'>
								<CardTitle className='orange-title'>
									{isSuperAdmin
										? 'All Social Profiles'
										: 'My Social Profiles'}
								</CardTitle>
								<Input
									placeholder='Search profiles...'
									value={searchTerm}
									onChange={(e) =>
										setSearchTerm(e.target.value)
									}
									className='max-w-md w-80'
								/>
							</div>
						</CardHeader>
						<CardContent>
							<ProfilesList
								groupedProfiles={groupedProfiles}
								expandedGroups={expandedGroups}
								setExpandedGroups={setExpandedGroups}
								toast={toast}
								user={user}
								isSuperAdmin={isSuperAdmin}
								profileAnalytics={profileAnalytics}
								fetchingAnalytics={fetchingAnalytics}
								onFetchAnalytics={handleFetchAnalytics}
							/>
						</CardContent>
					</Card>
				</div>
				<Card className='glow-card'>
					<CardHeader>
						<CardTitle className='pink-title'>
							Add Social Profile
							{/* {!isSuperAdmin && (
								<span className='block text-sm font-normal text-gray-500 mt-1'>
									{hasReachedProfileLimit
										? '⚠️ You have reached the maximum limit of 3 profiles'
										: `${3 - (filteredProfiles?.length || 0)} profile${
												3 -
													(filteredProfiles?.length ||
														0) ===
												1
													? ''
													: 's'
											} remaining`}
								</span>
							)} */}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{error && (
							<Alert
								variant='destructive'
								className='mb-4'
								style={{
									backgroundColor: '#FF8C00',
									color: 'white',
									border: '1px solid #FF6B00',
								}}
							>
								<AlertTitle>🔥 Error 🔥</AlertTitle>
								<AlertDescription
									style={{ whiteSpace: 'pre-line' }}
								>
									{error}
								</AlertDescription>
							</Alert>
						)}
						<div className='space-y-6 p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20'>
							<div className='space-y-2'>
								<Label className='text-sm font-semibold text-purple-300'>
									User Email
								</Label>
								<Input
									placeholder='Enter user email address...'
									value={userEmail}
									onChange={(e) =>
										setUserEmail(e.target.value)
									}
									className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 transition-all duration-200 hover:bg-black/40'
								/>
							</div>

							<div className='space-y-2'>
								<Label className='text-sm font-semibold text-purple-300'>
									Profile Title
								</Label>
								<Input
									placeholder='Enter a memorable profile title...'
									value={newProfileTitle}
									onChange={(e) =>
										setNewProfileTitle(e.target.value)
									}
									className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 transition-all duration-200 hover:bg-black/40'
								/>
							</div>

							<div className='p-4 bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-lg border border-green-500/20'>
								<div className='flex items-center space-x-3'>
									<Checkbox
										id='isCommunityProfile'
										checked={isCommunity}
										onCheckedChange={(checked) =>
											setIsCommunity(checked as boolean)
										}
										className='border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500'
									/>
									<Label
										htmlFor='isCommunityProfile'
										className='text-sm font-medium text-green-300 cursor-pointer'
									>
										Share with Watcher Community
										<p className='text-xs text-gray-400 mt-1'>
											Allow all community members to use
											this profile
										</p>
									</Label>
								</div>
							</div>

							<Button
								onClick={() => void handleCreateProfile()}
								disabled={isCreating}
								className='w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 text-lg rounded-full'
							>
								{isCreating ? (
									<>
										<svg
											className='animate-spin -ml-1 mr-3 h-5 w-5'
											xmlns='http://www.w3.org/2000/svg'
											fill='none'
											viewBox='0 0 24 24'
										>
											<circle
												className='opacity-25'
												cx='12'
												cy='12'
												r='10'
												stroke='currentColor'
												strokeWidth='4'
											></circle>
											<path
												className='opacity-75'
												fill='currentColor'
												d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
											></path>
										</svg>
										Creating...
									</>
								) : (
									<span className='inline-flex items-center gap-2'>
										<svg
											className='w-5 h-5'
											fill='none'
											stroke='currentColor'
											viewBox='0 0 24 24'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M12 4v16m8-8H4'
											/>
										</svg>
										Create Profile
									</span>
								)}
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
			<Confetti />
		</div>
	);
}

interface LinkStatus {
	status: string;
	platform: string;
	lastUpdated: string;
	details?: string;
}

const canonicalSocialPlatform = (platform: unknown): string => {
	const normalized = String(platform || '')
		.toLowerCase()
		.replace(/[\s_-]+/g, '');
	return ['x', 'twitter', 'twitterx', 'xtwitter'].includes(normalized)
		? 'twitter'
		: normalized;
};

function ProfilesList({
	groupedProfiles,
	expandedGroups,
	setExpandedGroups,
	toast,
	user,
	isSuperAdmin,
	profileAnalytics,
	fetchingAnalytics,
	onFetchAnalytics,
}: {
	groupedProfiles: Record<
		string,
		{ groupName: string; profiles: Profile[]; canEdit: boolean }
	>;
	expandedGroups: Record<string, boolean>;
	setExpandedGroups: React.Dispatch<
		React.SetStateAction<Record<string, boolean>>
	>;
	toast: any;
	user: any;
	isSuperAdmin: boolean;
	profileAnalytics: any;
	fetchingAnalytics: Set<string>;
	onFetchAnalytics: (profile: Profile) => Promise<void>;
}) {
	const [profileToDelete, setProfileToDelete] =
		React.useState<Profile | null>(null);
	const [deletingIds, setDeletingIds] = React.useState<Set<string>>(
		new Set()
	);
	const [regeneratingIds, setRegeneratingIds] = React.useState<Set<string>>(
		new Set()
	);
	const deleteProfileAction = useAction(api.uploadPost.deleteProfile);
	const deleteProfileMutation = useMutation(api.profiles.deleteProfile);
	const regenerateProfileUrlMutation = useAction(
		api.uploadPost.regenerateProfileUrl
	);
	const refreshProfileDetailsAction = useAction(
		api.uploadPost.refreshProfileDetails
	);

	const handleRegenerateUrl = async (profile: Profile) => {
		if (regeneratingIds.has(profile._id)) return;

		try {
			setRegeneratingIds((prev) => new Set([...prev, profile._id]));

			// WHY callerWallet (SAR-01-14): gated action; admin wallet authorizes
			// server-side via the admin fallback.
			await regenerateProfileUrlMutation({
				profileId: profile._id,
				callerWallet: user?.walletAddress ?? undefined,
			});
			await new Promise((resolve) => setTimeout(resolve, 2000));
			await refreshProfileDetailsAction({ profileId: profile._id });

			toast({
				title: '✨ Profile URL Updated ✨',
				description: 'Profile URL has been regenerated successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description:
					error instanceof Error
						? error.message
						: 'Failed to regenerate profile URL',
				variant: 'destructive',
				duration: 3000,
				style: { backgroundColor: '#FF8C00', color: 'white' },
			});
		} finally {
			setRegeneratingIds((prev) => {
				const next = new Set(prev);
				next.delete(profile._id);
				return next;
			});
		}
	};

	const handleDeleteConfirmed = async (profile: Profile) => {
		try {
			setDeletingIds((prev) => {
				const newSet = new Set(Array.from(prev));
				newSet.add(profile._id);
				return newSet;
			});
			// WHY callerWallet (SAR-01-14): gated action; admin wallet authorizes
			// server-side via the admin fallback.
			await deleteProfileAction({
				profileKey: profile.profileKey,
				callerWallet: user?.walletAddress ?? undefined,
			});
			await deleteProfileMutation({ profileId: profile._id });
			setProfileToDelete(null);
			toast({
				title: '✨ Profile Deleted ✨',
				description: 'Social profile successfully deleted',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description:
					error instanceof Error
						? error.message
						: 'Failed to delete profile',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setDeletingIds((prev) => {
				const newSet = new Set(Array.from(prev));
				newSet.delete(profile._id);
				return newSet;
			});
		}
	};

	return (
		<div className='space-y-8'>
			{Object.entries(groupedProfiles).map(
				([groupId, { groupName, profiles, canEdit }]) =>
					profiles.length > 0 && (
						<div key={groupId} className='space-y-4'>
								<div className='space-y-4'>
									<div className='flex items-center gap-2'>
										<motion.button
											aria-expanded={
												!!expandedGroups[groupId]
											}
											aria-label={`Toggle ${groupName} profiles`}
											onClick={() =>
												setExpandedGroups(
													(
													prev: Record<
														string,
														boolean
													>
												) => ({
													...prev,
													[groupId]: !prev[groupId],
												})
											)
											}
											className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
										animate={{
											rotate: expandedGroups[groupId]
												? 180
												: 0,
										}}
										transition={{
											duration: 0.2,
											ease: 'easeInOut',
										}}
										>
											{expandedGroups[groupId] ? '🔽' : '▶️'}
										</motion.button>
										<h2 className='text-3xl font-bold text-pink-500 uppercase'>
											{groupName} ({profiles.length})
										</h2>
									</div>
								</div>
							<p className='text-lg text-gray-500 mb-4'>
								Active Profiles: {profiles.length}
							</p>
							<AnimatePresence>
								{expandedGroups[groupId] && (
									<motion.div
										className={
											profiles.length > 10
												? 'space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar'
												: 'space-y-4'
										}
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.2 }}
									>
										{profiles.map((profile, idx) => (
											<motion.div
												key={profile._id}
												initial={{ opacity: 0, y: -10 }}
												animate={{
													opacity: 1,
													y: 0,
													transition: {
														duration: 0.2,
														delay: idx * 0.05,
													},
												}}
												exit={{
													opacity: 0,
													y: -10,
													transition: {
														duration: 0.15,
														delay:
															(profiles.length -
																idx -
																1) *
															0.05,
													},
												}}
											>
												{/* Always show ReactBits-style cards for all profiles */}
												<div className='space-y-6'>
													<div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
														{/* Show gamified cards for each platform */}
														{profile.linkStatuses
															?.filter((linkStatus) => {
																// Handle both new isLinked field and legacy status field
																const isConnected =
																	linkStatus.isLinked ??
																	linkStatus.status ===
																		'linked';
																return isConnected;
															})
															.map((linkStatus) => {
																// Find analytics for this platform
																const analytics =
																	profileAnalytics?.find(
																		(a: any) =>
																			a.profileId ===
																				profile._id &&
																			canonicalSocialPlatform(
																				a.platform
																			) ===
																				canonicalSocialPlatform(
																					linkStatus.platform
																				)
																	);

																return (
																	<ProfileCard3D
																		key={
																			linkStatus.platform
																		}
																		avatarUrl={
																			analytics?.profilePicture ||
																			`https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.profileTitle}-${linkStatus.platform}`
																		}
																		name={
																			analytics?.username ||
																			profile.profileTitle
																		}
																		title={`${linkStatus.platform.charAt(0).toUpperCase() + linkStatus.platform.slice(1)} Profile`}
																		handle={`@${analytics?.username || profile.profileTitle.toLowerCase().replace(/\s+/g, '')}`}
																		status={
																			analytics
																				? 'Analytics Active'
																				: 'Pending'
																		}
																		showUserInfo={
																			true
																		}
																		platform={
																			linkStatus.platform
																		}
																		metrics={
																			analytics
																				? {
																						followers:
																							analytics.followers,
																						engagement:
																							analytics.engagement,
																						views: analytics.views,
																					}
																				: undefined
																		}
																		contactText='View Details'
																		onContactClick={() =>
																			onFetchAnalytics(
																				profile
																			)
																		}
																		className='w-full'
																	/>
																);
															})}
													</div>

													{/* Button to manually fetch analytics */}
													<div className='flex justify-center'>
														<Button
															onClick={() =>
																onFetchAnalytics(
																	profile
																)
															}
															disabled={fetchingAnalytics.has(
																profile._id
															)}
															className='bg-purple-600 hover:bg-purple-700'
														>
															{fetchingAnalytics.has(
																profile._id
															) ? (
																<RefreshCw className='w-4 h-4 mr-2 animate-spin' />
															) : (
																<RefreshCw className='w-4 h-4 mr-2' />
															)}
															Fetch Analytics Data
														</Button>
													</div>

													{/* Original profile card for management - only show for owners or super admins */}
													{((profile.userId as unknown as string) ===
														(user?.id as unknown as string) ||
														isSuperAdmin) && (
														<ProfileCard
															profile={profile}
															onDelete={
																handleDeleteConfirmed
															}
															isDeleting={deletingIds.has(
																profile._id
															)}
															isRegenerating={regeneratingIds.has(
																profile._id
															)}
															onRegenerateUrl={
																handleRegenerateUrl
															}
															canEdit={canEdit}
															isOwner={
																(profile.userId as unknown as string) ===
																(user?.id as unknown as string)
															}
															isSuperAdmin={
																isSuperAdmin
															}
														/>
													)}
												</div>
											</motion.div>
										))}
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					)
			)}
			<ConfirmDialog
				isOpen={profileToDelete !== null}
				onClose={() => setProfileToDelete(null)}
				title={
					<div className='text-2xl font-bold text-red-600 flex items-center gap-2 animate-pulse'>
						🔥 Delete Social Profile 🔥
					</div>
				}
				description={
					<div className='text-lg text-red-500 font-semibold mt-2 text-center'>
						⚠️ Warning: This action cannot be undone! ⚠️
						<p className='text-base text-gray-600 mt-2'>
							Are you sure you want to permanently delete the
							profile:
							<span className='block mt-2 text-red-600 font-bold'>
								{profileToDelete?.profileTitle}
							</span>
						</p>
					</div>
				}
				onConfirm={() =>
					profileToDelete && handleDeleteConfirmed(profileToDelete)
				}
			/>
		</div>
	);
}

function ProfileCard({
	profile,
	onDelete,
	isDeleting,
	isRegenerating,
	onRegenerateUrl,
	canEdit,
	isOwner,
	isSuperAdmin,
}: {
	profile: Profile;
	onDelete: (profile: Profile) => void;
	isDeleting: boolean;
	isRegenerating: boolean;
	onRegenerateUrl: (profile: Profile) => void;
	canEdit: boolean;
	isOwner: boolean;
	isSuperAdmin: boolean;
}) {
	const { toast } = useToast();
	const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
	const [isRefreshing, setIsRefreshing] = React.useState(false);
	const [isUpdatingCommunity, setIsUpdatingCommunity] = React.useState(false);
	const refreshProfileDetailsAction = useAction(
		api.uploadPost.refreshProfileDetails
	);
	const updateCommunityAccess = useMutation(
		api.profiles.updateCommunityAccess
	);

	const handleRefreshDetails = async () => {
		try {
			setIsRefreshing(true);
			await refreshProfileDetailsAction({
				profileId: profile._id,
			});
			toast({
				title: '✨ Profile Refreshed ✨',
				description: 'Profile details have been refreshed successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description:
					error instanceof Error
						? error.message
						: 'Failed to refresh profile details',
				variant: 'destructive',
				duration: 3000,
				style: { backgroundColor: '#FF8C00', color: 'white' },
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleCommunityToggle = async (checked: boolean) => {
		try {
			setIsUpdatingCommunity(true);
			await updateCommunityAccess({
				profileId: profile._id,
				isCommunity: checked,
			});
			toast({
				title: checked ? '🌍 Profile Shared' : '🔒 Profile Private',
				description: checked
					? 'Profile is now shared with the community'
					: 'Profile is now private',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '🔥 Error 🔥',
				description: 'Failed to update community access',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsUpdatingCommunity(false);
		}
	};

	return (
		<Card className='p-4'>
			<div className='flex justify-between items-start'>
				<div>
					<div className='flex items-center gap-2 mb-2'>
						<h3 className='font-semibold'>
							{profile.profileTitle}
						</h3>
						<div className='flex flex-wrap gap-2'>
							{profile.linkStatuses?.map((linkStatus) => (
								<div
									key={linkStatus.platform}
									className='flex items-center gap-1'
								>
									<PlatformPill
										platform={linkStatus.platform}
										isLinked={
											linkStatus.status === 'linked'
										}
									/>
								</div>
							))}
							{(!profile.linkStatuses ||
								profile.linkStatuses.length === 0) && (
								<span className='text-xs text-gray-500 italic'>
									No platforms connected
								</span>
							)}
						</div>
					</div>
					<p className='text-sm text-gray-500 pb-3'>
						{profile.email.toLowerCase()}
					</p>
					<div className='flex flex-col gap-2'>
						{(isOwner || isSuperAdmin) && (
							<>
								<div className='flex items-center gap-2'>
									<p className='text-xs text-gray-400'>
										Profile Key:
									</p>
									<p className='text-xs font-mono text-gray-400'>
										{truncateKey(profile.profileKey)}
									</p>
									<Button
										variant='outline'
										size='sm'
										className='h-6 px-2 bg-gradient-to-r from-purple-400 to-pink-400 hover:from-purple-500 hover:to-pink-500 text-white font-semibold shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 rounded-full'
										onClick={() => {
											navigator.clipboard.writeText(
												profile.profileKey
											);
											toast({
												title: '✨ Copied!',
												description:
													'Profile key copied to clipboard',
												duration: 2000,
											});
										}}
									>
										<CopyIcon className='h-3 w-3' />
									</Button>
								</div>
								<div className='flex items-center gap-2'>
									<p className='text-xs text-gray-400 whitespace-nowrap'>
										Profile URL:
									</p>
									{!isRegenerating ? (
										profile.profileUrl ? (
											<a
												href={profile.profileUrl}
												target='_blank'
												rel='noopener noreferrer'
												className='text-xs font-mono text-blue-500 hover:text-blue-700 underline truncate max-w-[200px]'
												title={profile.profileUrl}
											>
												{profile.profileUrl.substring(
													0,
													50
												)}
												...
											</a>
										) : (
											<span className='text-xs font-mono text-gray-400 italic'>
												No URL set
											</span>
										)
									) : (
										<span className='text-xs font-mono text-gray-400 italic'>
											Generating new URL...
										</span>
									)}
									<div className='flex items-center gap-2 ml-auto'>
										<Button
											variant='outline'
											size='sm'
											className='bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
											onClick={() =>
												onRegenerateUrl(profile)
											}
											disabled={isRegenerating}
										>
											{isRegenerating ? (
												<>
													<svg
														className='animate-spin -ml-1 mr-3 h-4 w-4'
														xmlns='http://www.w3.org/2000/svg'
														fill='none'
														viewBox='0 0 24 24'
													>
														<circle
															className='opacity-25'
															cx='12'
															cy='12'
															r='10'
															stroke='currentColor'
															strokeWidth='4'
														></circle>
														<path
															className='opacity-75'
															fill='currentColor'
															d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
														></path>
													</svg>
													Regenerating...
												</>
											) : (
												<span className='flex items-center gap-2'>
													🔄 Generate URL
												</span>
											)}
										</Button>
										<Button
											variant='outline'
											size='sm'
											className='bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
											onClick={handleRefreshDetails}
											disabled={isRefreshing}
										>
											{isRefreshing ? (
												<>
													<svg
														className='animate-spin -ml-1 mr-3 h-4 w-4'
														xmlns='http://www.w3.org/2000/svg'
														fill='none'
														viewBox='0 0 24 24'
													>
														<circle
															className='opacity-25'
															cx='12'
															cy='12'
															r='10'
															stroke='currentColor'
															strokeWidth='4'
														></circle>
														<path
															className='opacity-75'
															fill='currentColor'
															d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
														></path>
													</svg>
													Refreshing...
												</>
											) : (
												<span className='flex items-center gap-2'>
													🔄 Refresh Details
												</span>
											)}
										</Button>
									</div>
								</div>
							</>
						)}
						{(isOwner || isSuperAdmin) && (
							<div className='flex items-center space-x-2 mt-3'>
								<Checkbox
									id={`community-${profile._id}`}
									checked={profile.isCommunity || false}
									onCheckedChange={handleCommunityToggle}
									disabled={isUpdatingCommunity || !canEdit}
								/>
								<Label
									htmlFor={`community-${profile._id}`}
									className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
								>
									Share with community
									{profile.isCommunity && (
										<span className='ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full'>
											🌍 Community
										</span>
									)}
								</Label>
							</div>
						)}
					</div>
				</div>
				{canEdit && (isOwner || isSuperAdmin) && (
					<Button
						variant='outline'
						size='sm'
						onClick={() => setShowConfirmDialog(true)}
						disabled={isDeleting}
						className='bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
					>
						{isDeleting ? (
							<>
								<svg
									className='animate-spin -ml-1 mr-3 h-5 w-5'
									xmlns='http://www.w3.org/2000/svg'
									fill='none'
									viewBox='0 0 24 24'
								>
									<circle
										className='opacity-25'
										cx='12'
										cy='12'
										r='10'
										stroke='currentColor'
										strokeWidth='4'
									></circle>
									<path
										className='opacity-75'
										fill='currentColor'
										d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
									></path>
								</svg>
								Deleting...
							</>
						) : (
							<span className='flex items-center gap-2'>
								🗑️ Delete
							</span>
						)}
					</Button>
				)}
			</div>
			<ConfirmDialog
				isOpen={showConfirmDialog}
				onClose={() => setShowConfirmDialog(false)}
				title={
					<div className='text-2xl font-bold text-red-600 flex items-center gap-2 animate-pulse'>
						🔥 Delete Social Profile 🔥
					</div>
				}
				description={
					<div className='text-lg text-red-500 font-semibold mt-2 text-center'>
						⚠️ Warning: This action cannot be undone! ⚠️
						<p className='text-base text-gray-600 mt-2'>
							Are you sure you want to permanently delete the
							profile:
							<span className='block mt-2 text-red-600 font-bold'>
								{profile.profileTitle}
							</span>
						</p>
					</div>
				}
				onConfirm={() => {
					onDelete(profile);
					setShowConfirmDialog(false);
				}}
			/>
		</Card>
	);
}

function StatusPill({ status }: { status: string }) {
	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center justify-center',
				status === 'linked'
					? '!bg-orange-300 !text-orange-700'
					: '!bg-gray-400 !text-gray-600'
			)}
		>
			{status === 'linked' ? 'LINKED' : 'NOT LINKED'}
		</span>
	);
}

function PlatformPill({
	platform,
	isLinked = false,
}: {
	platform: string;
	isLinked?: boolean;
}) {
	const linkedColors = {
		twitter: 'bg-purple-300 text-purple-700',
		facebook: 'bg-blue-300 text-blue-700',
		linkedin: 'bg-indigo-300 text-indigo-700',
		instagram: 'bg-pink-300 text-pink-700',
		youtube: 'bg-red-300 text-red-700',
		tiktok: 'bg-cyan-300 text-cyan-700', // Updated TikTok to a more interesting cyan color
	};

	const unlinkedColor = 'bg-gray-200 text-gray-500';

	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold',
				isLinked
					? linkedColors[platform as keyof typeof linkedColors] ||
							'bg-gray-300 text-gray-700'
					: unlinkedColor
			)}
		>
			{platform.toUpperCase()}
		</span>
	);
}
